import type { Legend } from "./types";

/** A colour ramp: value breakpoints paired with the colour at each. */
export interface Ramp {
  stops: { value: number; color: string }[];
  /** Rendered into the legend and popups. */
  unit: string;
  format: (v: number) => string;
}

/**
 * Price per m². Sequential yellow→red (ColorBrewer YlOrRd), which stays legible
 * over the light grey basemap and reads as "more = hotter" without needing a
 * legend to decode.
 *
 * Breaks are fixed rather than quantile-based on purpose: a buyer comparing
 * Meaux with the 6th arrondissement needs the colours to mean the same number
 * everywhere, and to stay put when the year changes.
 */
export const PRICE_RAMP: Ramp = {
  unit: "€/m²",
  format: (v) => `${(v / 1000).toFixed(v < 10000 ? 1 : 0)}k`,
  stops: [
    { value: 2000, color: "#ffffb2" },
    { value: 4000, color: "#fed976" },
    { value: 6000, color: "#feb24c" },
    { value: 8000, color: "#fd8d3c" },
    { value: 10000, color: "#fc4e2a" },
    { value: 13000, color: "#e31a1c" },
    { value: 16000, color: "#b10026" },
  ],
};

/** Build a MapLibre `interpolate` expression from a ramp. */
export function rampExpression(ramp: Ramp, valueExpr: unknown): unknown[] {
  return [
    "interpolate",
    ["linear"],
    valueExpr,
    ...ramp.stops.flatMap((s) => [s.value, s.color]),
  ];
}

/** Build the legend rows for a ramp, labelled as ranges. */
export function rampLegend(ramp: Ramp, note?: string): Legend {
  const stops = ramp.stops.map((s, i) => {
    const next = ramp.stops[i + 1];
    const label = next
      ? `${ramp.format(s.value)} – ${ramp.format(next.value)}`
      : `${ramp.format(s.value)}+`;
    return { color: s.color, label };
  });
  return { unit: ramp.unit, stops, note };
}
